import type { VerificationQuestionView } from "@credit-acceptance/step6-bff-client";

import type { CallScriptVerifiedFields } from "./callScript.types";

/**
 * Question text for each verified field, used to match a field to its
 * `verificationQuestionPk` in the catalog returned by
 * `getVerificationQuestions`. Fields with no counterpart in the catalog
 * (`differentWorkLocation`) are omitted — no answer row is ever built for
 * them.
 */
export const CALL_SCRIPT_QUESTION_TEXT: Partial<Record<keyof CallScriptVerifiedFields, string>> = {
  employerName: "Employer Name",
  phoneNumberDialed: "Phone Number Dialed",
  verificationOutsourced: "Verification Outsourced?",
  originalNumberBad: "Was original number bad?",
  verifiedWith: "Verified With(names & job title)",
  hireDate: "Hire Date(Required for RPC)",
  employmentType: "Emp Type(W2, 1099, Temp, Not Emp)",
  occupation: "Occupation",
  streetNumber: "Employee Physical Address Street No",
  streetName: "Employee Physical Address Street Name",
  apartmentNumber: "Employee Physical Address Apt",
  city: "Employee Physical Address City",
  state: "Employee Physical Address State",
  zip: "Employee Physical Address Zip",
  employerPhone: "Employer Verification Phone",
  workLocationPhone: "Physical Work Location Phone",
  dateOfTermination: "Date of Termination(if applicable)",
  payFrequency: "Pay Frequency",
  grossPayPerPeriod: "Gross Pay Per Period",
  avgHoursPerWeek: "Avg hours worked per week",
};

/**
 * Resolves the `type` code for the questions catalog lookup from the
 * applicant role and income slot: `EVIDQ{n}` for the driver, `EVISSQ{n}` for
 * the second signer, where `n` is the 1-based income slot.
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @param {number} incomeIndex - Zero-based income slot index.
 * @returns {string} The `type` query value for `getVerificationQuestions`.
 */
export function resolveVerificationQuestionType(personPath: "driver" | "secondSigner", incomeIndex: number): string {
  const slot = incomeIndex + 1;
  return personPath === "driver" ? `EVIDQ${slot}` : `EVISSQ${slot}`;
}

const MAX_INCOME_SLOTS = 3;

/**
 * The fixed set of verification-question `type` codes for a given role (3
 * income slots). Used both to prefetch/cache the full catalog up front and to
 * resolve a role's answer pks from all 3 slots at save time, since a driver's
 * or second signer's income can live in any of the 3 slots.
 * @param {"driver" | "secondSigner"} personPath - Which applicant.
 * @returns {ReadonlyArray<string>} The role's 3 `type` codes.
 */
export function questionTypesForPerson(personPath: "driver" | "secondSigner"): readonly string[] {
  return Array.from({ length: MAX_INCOME_SLOTS }, (_, index) => resolveVerificationQuestionType(personPath, index));
}

/**
 * The fixed set of verification-question `type` codes the Call Script modal
 * can ever request (3 income slots per role). Global reference data — safe
 * to prefetch and cache in full up front instead of one type at a time.
 */
export const ALL_CALL_SCRIPT_QUESTION_TYPES: readonly string[] = (["driver", "secondSigner"] as const).flatMap(
  (personPath) => questionTypesForPerson(personPath)
);

/**
 * Builds a field-key -> verificationQuestionPk lookup by matching each
 * field's question text (case-insensitively) against the fetched catalog.
 * @param {VerificationQuestionView[]} questions - Catalog entries for a given `type`.
 * @returns {Partial<Record<keyof CallScriptVerifiedFields, number>>} Resolved pks, keyed by field.
 */
export function buildQuestionPkMap(
  questions: VerificationQuestionView[]
): Partial<Record<keyof CallScriptVerifiedFields, number>> {
  const byLowerText = new Map<string, number>();
  for (const question of questions) {
    if (question.question && question.verificationQuestionPk !== undefined) {
      byLowerText.set(question.question.toLowerCase(), question.verificationQuestionPk);
    }
  }

  const pkMap: Partial<Record<keyof CallScriptVerifiedFields, number>> = {};
  for (const [field, text] of Object.entries(CALL_SCRIPT_QUESTION_TEXT) as Array<
    [keyof CallScriptVerifiedFields, string]
  >) {
    const pk = byLowerText.get(text.toLowerCase());
    if (pk !== undefined) {
      pkMap[field] = pk;
    }
  }
  return pkMap;
}
